import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Location } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { PLAYING } from '@disco/constants/MediaStatus'
import {
  clientLibraryDiscoveryPanelFavorite,
  clientLibraryDiscoveryPanelRequestAccess,
} from '@disco/state-management/actions/client-library-discovery-panel-actions'
import {
  pauseTrack,
  playTrack,
} from '@disco/state-management/actions/MediaActions'
import { getShortlistsOpen } from '@disco/state-management/selectors/app'
import {
  getTrackPlaybackStatus,
  getTrackPlaying,
} from '@disco/state-management/selectors/media'
import { sendDiscoverMixpanelEvent } from '@disco/utils/mixpanel/discover-page'

import type { ClientLibraryDiscoveryPanelItem } from 'types/client-library'
import type { Track } from 'types/track'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type {
  CatalogNavigationState,
  ContentType,
  NavigationState,
  PlaylistNavigationState,
} from '../utils'
import { canRequestAccess, hasUserAccess } from '../utils'

export const useContentData = (
  location: Location,
  contentType: ContentType,
) => {
  const navigationState = location.state as NavigationState

  const playlist = useMemo(() => {
    if (contentType !== 'playlist' || !navigationState) return null
    return (navigationState as PlaylistNavigationState).playlist || null
  }, [contentType, navigationState])

  const catalog = useMemo(() => {
    if (contentType !== 'catalog' || !navigationState) return null
    return (navigationState as CatalogNavigationState).catalog || null
  }, [contentType, navigationState])

  const hasData = contentType === 'playlist' ? !!playlist : !!catalog

  return { playlist, catalog, navigationState, hasData }
}

export const useContentPlayback = (
  tracks: Track[],
  contentId: number,
  handleSetAudioMixpanelPayloadPlayAll?: () => void,
) => {
  const dispatch = useDispatch()

  const trackIds = useMemo(
    () => tracks.filter(t => t && t.id).map(t => t.id),
    [tracks],
  )

  // Memoize the Set for O(1) lookup performance
  const trackIdSet = useMemo(() => new Set(trackIds), [trackIds])

  const currentlyPlayingTrack = useSelector(getTrackPlaying)

  // Check if current track is in our list (memoized to avoid selector recomputation)
  const isCurrentTrackInList = useMemo(
    () =>
      currentlyPlayingTrack ? trackIdSet.has(currentlyPlayingTrack.id) : false,
    [currentlyPlayingTrack, trackIdSet],
  )

  // Only fetch playback status if the current track is in our list
  const currentTrackId = isCurrentTrackInList ? currentlyPlayingTrack?.id : null
  const currentTrackPlaybackStatus = useSelector(state =>
    currentTrackId
      ? getTrackPlaybackStatus(state, { trackId: currentTrackId })
      : null,
  )

  const isCurrentlyPlaying = useMemo(() => {
    if (!currentTrackPlaybackStatus) return false
    return currentTrackPlaybackStatus.playbackStatus === PLAYING
  }, [currentTrackPlaybackStatus])

  const trackToPauseId = currentlyPlayingTrack?.id

  const handlePlayAll = useCallback(() => {
    if (tracks.length === 0) return

    if (isCurrentlyPlaying && trackToPauseId) {
      dispatch(pauseTrack(trackToPauseId))
      return
    }

    dispatch(
      playTrack(tracks[0].id, 'in-app', {
        tracklist: trackIds,
        playbackLocation: 'discover-music-home',
        clientLibraryId: contentId,
      }),
    )

    if (handleSetAudioMixpanelPayloadPlayAll) {
      handleSetAudioMixpanelPayloadPlayAll()
    }
  }, [
    tracks,
    trackIds,
    trackToPauseId,
    isCurrentlyPlaying,
    contentId,
    dispatch,
    handleSetAudioMixpanelPayloadPlayAll,
  ])

  return { isCurrentlyPlaying, handlePlayAll, trackIds }
}

export const useCatalogActions = (
  catalog: ClientLibraryDiscoveryPanelItem | null,
  catalogBusinessName?: string,
) => {
  const dispatch = useDispatch()
  const [isFavorite, setIsFavorite] = useState<boolean>(false)

  const catalogStatus = catalog?.status ?? null
  const catalogAccessLevel = catalog?.discovery_access_level ?? null

  const hasAccess = hasUserAccess(catalogStatus, catalogAccessLevel)

  useEffect(() => {
    if (catalog) {
      setIsFavorite(catalog.is_favorite)
    }
  }, [catalog])

  const handleFavoriteClick = useCallback(() => {
    if (catalog) {
      const newFavoriteState = !isFavorite
      setIsFavorite(newFavoriteState)
      dispatch(
        clientLibraryDiscoveryPanelFavorite(catalog.id, newFavoriteState),
      )
      sendDiscoverMixpanelEvent('Click Button', {
        copy: catalog.is_favorite ? 'Unfavorite Catalog' : 'Favorite Catalog',
        catalogId: catalog.id,
        catalogName: catalog?.display_name || 'Untitled Catalog',
        catalogBusiness: catalogBusinessName || 'Unknown',
        context: 'Catalog page',
      })
    }
  }, [catalog, dispatch, isFavorite, catalogBusinessName])

  const handleRequestAccess = useCallback(() => {
    if (catalog && canRequestAccess(hasAccess, catalog.status)) {
      dispatch(
        clientLibraryDiscoveryPanelRequestAccess(catalog.id, catalog.status),
      )
      sendDiscoverMixpanelEvent('Click Button', {
        copy: 'Request Access',
        catalogId: catalog.id,
        catalogName: catalog?.display_name || 'Untitled Catalog',
        catalogBusiness: catalogBusinessName || 'Unknown',
        context: 'Catalog page',
      })
    }
  }, [catalog, hasAccess, dispatch, catalogBusinessName])

  return {
    isFavorite,
    hasAccess,
    catalogStatus,
    handleFavoriteClick,
    handleRequestAccess,
  }
}

export const useIsPlaylistCreatorOpen = () => {
  return useSelector(getShortlistsOpen)
}

export const useRedirectIfNoData = (hasData: boolean) => {
  const navigate = useNavigate()

  useEffect(() => {
    if (!hasData) {
      navigate('/discover', { replace: true })
    }
  }, [hasData, navigate])
}
